// Guide — interaktiver Handbuch der Ember-Anwendung.
// Inhalt entspricht 1:1 dem Markdown-Handbuch unter
// apps/web/public/guide/embertime-handbuch.md, das ebenfalls die Basis
// für die generierte PDF (apps/api/scripts/build-guide-pdf.cjs) bildet.
// Screenshots werden in apps/web/public/guide/screenshots/<slug>.png
// abgelegt und automatisch eingeblendet, sobald sie vorhanden sind.
import { useQuery } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ArrowUp, BookOpen, FileText, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Input } from '../components/ui/input'
import { BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { SidePanel } from '../components/ui/side-panel'
import { api } from '../lib/api'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guide',
  component: GuidePage,
})

interface GuideSection {
  id: string
  title: string
  category: string
  description: string
  screenshot?: string
  highlights?: string[]
}

const SECTIONS: GuideSection[] = [
  // 2. Dashboard & Widgets
  {
    id: 'dashboard-overview',
    title: 'Dashboard / Übersicht',
    category: 'Dashboard & Widgets',
    description:
      'Zentraler Einstiegspunkt nach dem Login. Frei konfigurierbares Raster aus Widgets — Stoppuhr, Wochenfortschritt, Anträge, Stundenkonto, Meetings, Mails, Chat-Erwähnungen und mehr. Layout wird pro Person serverseitig gespeichert; beim ersten Aufruf greift die zugewiesene Vorlage.',
    highlights: [
      'Bearbeiten-Modus mit Drag & Drop und Größenänderung',
      'Widget-Bibliothek mit ca. 20 Bausteinen',
      'Vorlagen rollen- oder teamweise zuweisbar',
      'Berechtigungen respektieren die Modul-Sichtbarkeit',
    ],
  },
  {
    id: 'widget-registry',
    title: 'Widget-Bibliothek',
    category: 'Dashboard & Widgets',
    description:
      'Sammlung aller verfügbaren Widgets: Stoppuhr, Wochenfortschritt, Anträge, Stundenkonto, Kalender heute, letzte Tätigkeiten, Mail-Posteingang, Chat-Erwähnungen, Disposition heute, Tickets, Kanban-Übersicht. Jedes Widget lädt eigene Daten und respektiert Berechtigungen.',
    highlights: [
      'Echtzeit-Aktualisierung pro Widget',
      'Widgets sind frei kombinier- und positionierbar',
      'Sichtbarkeit folgt den Modul-Berechtigungen',
    ],
  },

  // 3. Zeiterfassung
  {
    id: 'timer',
    title: 'Stoppuhr',
    category: 'Zeiterfassung',
    description:
      'Echtzeit-Erfassung: Projekt wählen, Tätigkeit eintragen, Tags setzen und Start drücken. Die Stoppuhr läuft serverseitig weiter — auch wenn der Browser geschlossen wird. Zuletzt verwendete Projekte beschleunigen wiederkehrende Buchungen.',
    highlights: [
      'Start / Pause / Stopp mit Pausen-Historie',
      'Schnellauswahl letzter Projekte',
      'Tag-Picker mit Auto-Vervollständigung',
      'Inline-Bearbeitung der heutigen Einträge',
    ],
  },
  {
    id: 'calendar',
    title: 'Kalender',
    category: 'Zeiterfassung',
    description:
      'Visualisierung aller Zeiteinträge und Abwesenheiten in Wochen-, Monats- und Jahresansicht. Neue Einträge entstehen per Klick-und-Ziehen direkt im Stundenraster. Pausenregeln werden automatisch nach den gesetzlichen Tier-Schwellen angewandt.',
    highlights: [
      'Wochenraster mit Drag-zum-Anlegen',
      'Monatsansicht als Heatmap',
      'Jahresansicht mit Abwesenheits-Übersicht',
      'Wochen-Footer mit Soll / Ist / Saldo',
    ],
  },
  {
    id: 'entries',
    title: 'Einträge',
    category: 'Zeiterfassung',
    description:
      'Tabellarische Liste aller eigenen Buchungen mit gemeinsamen Filtern (Zeitraum, Projekt, Kunde, Suche, Tags). Ideal für nachträgliche Korrekturen, Bulk-Bearbeitung und das Erstellen abrechenbarer Berichte.',
    highlights: [
      'Sortier- und filterbar in allen Spalten',
      'Inline-Bearbeitung per Stift-Symbol',
      'Bulk-Aktionen (Tag setzen, Projekt ändern, Löschen)',
      'Pagination mit anpassbarer Seitengröße',
    ],
  },
  {
    id: 'time-accounts',
    title: 'Stundenkonten',
    category: 'Zeiterfassung',
    description:
      'Saldo-Übersicht über mehrere Konten: Gleitzeit, Urlaubsanspruch, Krankheits-Konto, sonstige Kontingente. Pro Monat aufgeschlüsselt mit Zu- und Abgängen sowie Trendpfeilen.',
    highlights: [
      'Monatsnavigation per Pfeiltasten',
      'Soll / Ist-Vergleich mit Trend',
      'Detail-Aufklappen für Einzelbewegungen',
      'Kategorien: Gleitzeit, Urlaub, Krank, Sonderurlaub',
    ],
  },
  {
    id: 'requests',
    title: 'Anträge',
    category: 'Zeiterfassung',
    description:
      'Anlage und Verwaltung aller Abwesenheits-Anträge: Urlaub, Krank, Sonderurlaub, Gleitzeit, unbezahlt. Genehmigende sehen zusätzlich eingehende Anträge des eigenen Teams. Komplette Historie inklusive Soft-Delete-Einträge.',
    highlights: [
      'Antrag stellen mit automatischer Arbeitstag-Berechnung',
      'Status-Workflow Eingereicht → Genehmigt / Abgelehnt',
      'Rücknahme durch Antragsteller möglich',
      'Stornierung nach Genehmigung mit Freigabe',
    ],
  },
  {
    id: 'absences',
    title: 'Abwesenheiten (Team-Sicht)',
    category: 'Zeiterfassung',
    description:
      'Für Teamleitungen alle Abwesenheiten der eigenen Mitarbeitenden auf einen Blick. Engpässe (mehrere Personen gleichzeitig im Urlaub) werden visuell hervorgehoben; Genehmigungen sind direkt aus der Liste möglich.',
    highlights: [
      'Teamleitungs- und Admin-Sicht',
      'Hervorhebung von Urlaubs-Engpässen',
      'Quick-Genehmigung pro Antrag',
    ],
  },

  // 4. Projektarbeit
  {
    id: 'customers',
    title: 'Kunden',
    category: 'Projektarbeit',
    description:
      'Auftraggeber des Unternehmens mit Adresse, Steuer-/USt-ID, Kontaktdaten, Notizen und Anhängen. Statt zu löschen werden Kunden archiviert — historische Daten bleiben verknüpft.',
    highlights: [
      'Anlegen, bearbeiten, archivieren',
      'Manuelle Gruppen zur freien Strukturierung',
      'Bulk-Aktionen (Tag setzen, Gruppe zuweisen)',
      'Spalten konfigurierbar im Ansichts-Switcher',
    ],
  },
  {
    id: 'projects',
    title: 'Projekte',
    category: 'Projektarbeit',
    description:
      'Zentrale Buchungs-Einheit. Jedes Projekt gehört zu einem Kunden, hat einen Status, optional eine Standard-Leistung, Budget-Grenzen und ein Team. Archivierte Projekte sperren Buchungen, behalten aber die Historie.',
    highlights: [
      'Pflichtfelder Kunde + Name, optional Budget',
      'Team-Zuordnung steuert Sichtbarkeit beim Buchen',
      'Tags pro Projekt mit automatischer Übernahme',
      'Archivieren statt Löschen',
    ],
  },
  {
    id: 'services',
    title: 'Leistungen',
    category: 'Projektarbeit',
    description:
      'Tätigkeitskatalog (Entwicklung, Beratung, Konzeption …). Jede Leistung hat einen Standardpreis und eine Abrechnungs-Eigenschaft (abrechenbar / intern). Bei Rechnungserstellung sind Preise pro Position überschreibbar.',
    highlights: [
      'Stunden-Standardpreis',
      'Standard-Aufgabe pro Projekt setzbar',
      'Tags und manuelle Gruppen',
    ],
  },
  {
    id: 'products',
    title: 'Produkte',
    category: 'Projektarbeit',
    description:
      'Festpreis-Artikel ohne Zeitbezug (Lizenzen, Hardware, Pauschalen). Können einer Rechnung als zusätzliche Position hinzugefügt werden. Inventar-Verknüpfung für lagergeführte Artikel.',
    highlights: [
      'Einheit + Nettopreis + Steuersatz',
      'Inventar-Verknüpfung optional',
      'Bulk-Aktionen wie bei Kunden / Leistungen',
    ],
  },
  {
    id: 'stammdaten',
    title: 'Stammdaten-Übersicht',
    category: 'Projektarbeit',
    description:
      'Tab-basierte Hub-Seite, die Kunden, Tätigkeiten und Tags an einer Stelle bündelt. Spart Sidebar-Platz; die direkten URLs bleiben gleichwertig erreichbar.',
    highlights: [
      'Drei Tabs: Kunden, Tätigkeiten, Tags',
      'Deep-Links auf die Einzel-Seiten bleiben gültig',
    ],
  },
  {
    id: 'tags',
    title: 'Tags',
    category: 'Projektarbeit',
    description:
      'Frei definierbare Schlagworte, die an Zeiteinträgen, Projekten, Kunden, Leistungen und Tickets hängen können. Farben helfen bei der visuellen Unterscheidung; Filter im Report greifen direkt darauf.',
    highlights: [
      'Pro Tag Name, Farbe, optionale Beschreibung',
      'Tag-basierte Filter in der Auswertung',
      'Massen-Tags über Bulk-Aktionen',
    ],
  },

  // 5. Kommunikation
  {
    id: 'chat',
    title: 'Chat',
    category: 'Kommunikation',
    description:
      'Echtzeit-Chat mit Direktnachrichten und Channels. Nachrichten werden serverseitig gespeichert; Bookmarks und @-Erwähnungen erleichtern die Wiederauffindbarkeit. Datei-Anhänge und Code-Snippets mit Syntax-Highlight möglich.',
    highlights: [
      'Channels öffentlich oder privat',
      '1:1- und Gruppen-Direktnachrichten',
      'Erwähnungen erzeugen Benachrichtigungen',
      'Bookmarks für wichtige Nachrichten',
    ],
  },
  {
    id: 'mail',
    title: 'Mail',
    category: 'Kommunikation',
    description:
      'Vollständiger IMAP/SMTP-Client direkt im Browser. Mehrere Konten parallel, Ordner-Synchronisation, sicheres HTML-Rendering. OAuth2 für Google und Microsoft 365 sowie klassische IMAP-Authentifizierung.',
    highlights: [
      'OAuth2 für Google / Microsoft 365',
      'Verfassen, Antworten, Weiterleiten (HTML + Text)',
      'Anhänge öffnen und speichern',
      'Ordner verschieben, markieren, archivieren',
    ],
  },
  {
    id: 'meetings',
    title: 'Meetings',
    category: 'Kommunikation',
    description:
      'Termin- und Videokonferenz-Planung mit eingebauter Jitsi-Anbindung. Wer ein Meeting anlegt, erhält automatisch einen JWT-signierten Beitritts-Link. Wiederkehrende Termine über Serien-Regeln.',
    highlights: [
      'Titel, Datum, Dauer, Teilnehmende, Ort / Link',
      'Jitsi-Integration mit JWT-Token',
      'Zu- und Absage-Status pro Teilnehmer',
      'Serien-Termine möglich',
    ],
  },
  {
    id: 'notifications',
    title: 'Benachrichtigungen',
    category: 'Kommunikation',
    description:
      'Zentrale Notification-Box für Antrags-Updates, Erwähnungen, Ticket-Zuweisungen, Fetcher-Läufe und mehr. Browser-Push wird optional als PWA unterstützt — auf jedem Gerät separat aktivierbar.',
    highlights: [
      'Lesen / als gelesen markieren (einzeln oder alle)',
      'Filter nach Typ',
      'Push-Aktivierung pro Gerät',
    ],
  },

  // 6. Auswertung
  {
    id: 'auswertung',
    title: 'Reports',
    category: 'Auswertung',
    description:
      'Tab-basierte Berichts-Seite mit zwei Ansichten: Einträge-Liste mit gemeinsamen Filtern sowie Zeitraum-Report mit Aggregaten (Stunden pro Projekt, Kunde, Person, Tag). Drilldown vom Aggregat auf einzelne Einträge.',
    highlights: [
      'Gemeinsamer Filter über beide Tabs',
      'CSV-Export',
      'Drilldown vom Aggregat auf Einzeleinträge',
    ],
  },
  {
    id: 'reports',
    title: 'Reports (klassisch)',
    category: 'Auswertung',
    description:
      'Klassische Bericht-Seite mit erweiterten Kennzahlen: Soll/Ist-Vergleich, Krank-Quote, Überstunden-Trend, Top-Projekte und Top-Tags. Ergebnisse als CSV exportierbar.',
    highlights: [
      'Soll/Ist-Vergleich',
      'Krank-Quote und Überstunden-Trend',
      'Top-Projekte / Top-Tags',
      'CSV-Export',
    ],
  },
  {
    id: 'activity-log',
    title: 'Verlauf',
    category: 'Auswertung',
    description:
      'Lückenloses Audit-Log aller relevanten Aktionen — Anlegen, Ändern, Löschen, Genehmigen, Stornieren. Mitarbeitende sehen die eigene Historie, Teamleitungen ihr Team, Admins alles. Soft-Delete-Aktionen sind rückgängig machbar.',
    highlights: [
      'Filter nach Typ, Nutzer, Zeitraum',
      'Vorher / Nachher-Diff im Detail',
      'Rückgängig-Funktion für Soft-Deletes',
    ],
  },

  // 7. Rechnungen
  {
    id: 'invoices',
    title: 'Rechnungsliste',
    category: 'Rechnungen',
    description:
      'Alle Rechnungen mit Status, Betrag, Kunde, Fälligkeit. Status-Workflow: Entwurf → Versendet → Bezahlt / Storniert. PDF-Vorschau und CSV-Export für die Buchhaltung verfügbar.',
    highlights: [
      'PDF-Vorschau je Rechnung',
      'CSV-Export für die Buchhaltung',
      'Löschen nur im Entwurfsstatus',
      'Doppelklick öffnet die Detailansicht',
    ],
  },
  {
    id: 'invoices-detail',
    title: 'Rechnungs-Detail',
    category: 'Rechnungen',
    description:
      'Vollständige Bearbeitung einer Rechnung inklusive Positionen, Zahlungsbedingungen, Notizen und Anhängen. Mehrere PDF-Layouts wählbar; XRechnung und ZUGFeRD werden als zusätzliche Formate beigelegt.',
    highlights: [
      'Positionen inline bearbeiten',
      'PDF-Layout wählbar',
      'XRechnung + ZUGFeRD automatisch beigelegt',
      'Versand-Historie sichtbar',
    ],
  },
  {
    id: 'invoices-new',
    title: 'Neue Rechnung',
    category: 'Rechnungen',
    description:
      'Geführter Wizard: Kunde wählen → Zeitraum eingrenzen → Positionen aus Zeiteinträgen und Produkten zusammenstellen → Vorschau prüfen → speichern. §19-UStG-Hinweis automatisch bei Kleinunternehmer-Konfiguration.',
    highlights: [
      'Smart-Vorschlag auf Basis offener Buchungen',
      'Stunden-Pauschalierung und Rabatte',
      'Steuersatz pro Position überschreibbar',
      'Kleinunternehmer-Hinweis automatisch',
    ],
  },
  {
    id: 'invoice-layout',
    title: 'Rechnungs-Layout',
    category: 'Rechnungen',
    description:
      'Konfiguration des PDF-Templates: Logo, Farben, Schriftarten, Kopf-/Fußzeilen, Sidebar-Layout, Bandheader. Verschiedene Standard-Stile wie Modern, Klassisch und Minimal stehen zur Auswahl. Nur Admin.',
    highlights: [
      'Logo + Farben + Schriftarten',
      'Standard-Stile (Modern / Klassisch / Minimal)',
      'Kopf- und Fußzeilen-Editor',
    ],
  },

  // 8. Dokumente
  {
    id: 'documents',
    title: 'Dateien',
    category: 'Dokumente',
    description:
      'Zentrale Dokumentenablage für Belege, Verträge, Lieferscheine und beliebige weitere Dateien. Drag-and-Drop-Upload; automatische QR-Code-Erkennung für Beleg-Verknüpfung. OCR und Volltext-Suche im Hintergrund.',
    highlights: [
      'Drag-and-Drop-Upload mit Tags',
      'OCR für gescannte PDFs',
      'Verknüpfung zu Projekt / Kunde / Rechnung',
      'Versionen pro Datei',
    ],
  },
  {
    id: 'documents-detail',
    title: 'Dokument-Detail',
    category: 'Dokumente',
    description:
      'Inline-Vorschau, Anmerkungen, Verknüpfungen, OCR-Text, Versionsliste, Freigabe-Status. Alle Aktionen werden im Audit-Log protokolliert.',
    highlights: ['Inline-PDF-/Bild-Vorschau', 'OCR-Text einsehbar', 'Verknüpfungs-Editor'],
  },
  {
    id: 'fetcher',
    title: 'Beleg-Fetcher',
    category: 'Dokumente',
    description:
      'Automatisches Abholen von Rechnungen aus angebundenen Portalen (z. B. Amazon Business). Playwright-Sessions laufen serverseitig in einem konfigurierten Intervall. 2FA-Codes über QR-Code-Workflow eingebbar. Nur Admin.',
    highlights: [
      'Anbieter konfigurieren (Login, Intervall)',
      '2FA-QR-Code-Workflow',
      'Steuersatz-Erkennung aus Bestelldaten',
      'Dedup bereits importierter Belege',
    ],
  },

  // 9. Personalverwaltung
  {
    id: 'teams',
    title: 'Teams',
    category: 'Personalverwaltung',
    description:
      'Teams gruppieren Mitarbeitende für Genehmigungs-Workflows, Sichtbarkeits-Regeln und Reports. Pro Team eine Leitung, mehrere Mitglieder. Mitglieder per Picker hinzufügen oder entfernen.',
    highlights: [
      'Name, Beschreibung, Teamleitung',
      'Mitglieder hinzufügen / entfernen',
      'Rolle innerhalb des Teams (Mitglied / Leitung)',
    ],
  },
  {
    id: 'users',
    title: 'Personen',
    category: 'Personalverwaltung',
    description:
      'Vollständige Mitarbeiterverwaltung mit Anlage, Rollenzuweisung, Passwort-Reset, Soft-Delete und Dossier-Aufruf. Initial-Passwort wird vergeben oder per Mail versendet.',
    highlights: [
      'Neuen Mitarbeitenden anlegen',
      'Rolle zuweisen (Admin / Custom / User)',
      'Teamzugehörigkeit verwalten',
      'Dossier öffnen mit Übersicht',
    ],
  },
  {
    id: 'org',
    title: 'Organisation',
    category: 'Personalverwaltung',
    description:
      'Globale Stamm-Konfiguration des Unternehmens: Firmenname, Anschrift, Steuernummer, Bankdaten, Standard-Sprache, Standard-Zeitzone. Nur Admin.',
    highlights: ['Firmenstammdaten', 'Steuer- und Bankdaten', 'Standard-Sprache und Zeitzone'],
  },
  {
    id: 'roles',
    title: 'Rollen',
    category: 'Personalverwaltung',
    description:
      'Anlegen eigener Rollen mit feingranularen Berechtigungen (aktuell 16 Einzelrechte) und Sidebar-Sichtbarkeit pro Modul. Admin-Flag schaltet alle Rechte frei. Nur Admin.',
    highlights: [
      'Rolle mit Name, Beschreibung, Permissions',
      'Sidebar-Sichtbarkeit pro Modul',
      'Admin-Flag als Master-Switch',
      'Zuweisung über die Personen-Seite',
    ],
  },
  {
    id: 'holidays',
    title: 'Feiertage',
    category: 'Personalverwaltung',
    description:
      'Regionale Feiertage je Bundesland plus eigene firmenweite Feiertage (z. B. Betriebsruhe). Feiertage reduzieren das Soll automatisch. Eigene Feiertage optional jährlich wiederkehrend.',
    highlights: [
      'Region wählen (DE-BY, DE-BW, AT, CH, …)',
      'Eigene Feiertage hinzufügen',
      'Monatsnavigation für Detail-Sicht',
    ],
  },
  {
    id: 'blackouts',
    title: 'Sperrzeiten',
    category: 'Personalverwaltung',
    description:
      'Zeiträume, in denen kein Urlaub genehmigt werden darf (Jahresabschluss, Messen). Anträge in Sperrzeiten werden markiert und können nur mit Begründung genehmigt werden. Optional pro Team oder firmenweit.',
    highlights: [
      'Zeitraum mit Start, Ende, Begründung',
      'Optional pro Team',
      'Markierung im Antrags-Workflow',
    ],
  },
  {
    id: 'vacation-rules',
    title: 'Urlaubsregeln',
    category: 'Personalverwaltung',
    description:
      'Globale Urlaubs-Standardregeln: Anzahl Tage pro Jahr, Carry-Over-Politik, anteilige Berechnung bei Eintritt mitten im Jahr, Sonderurlaub-Typen. Nur Admin.',
    highlights: [
      'Tage pro Jahr',
      'Carry-Over-Konfiguration',
      'Anteilige Berechnung bei Eintritt unterjährig',
    ],
  },
  {
    id: 'surcharges',
    title: 'Zuschläge',
    category: 'Personalverwaltung',
    description:
      'Zuschlagsregeln für Nacht-, Wochenend-, Feiertags- oder Überstundenarbeit. Berechnen sich automatisch in den Stundenkonten und können in Rechnungen einfließen.',
    highlights: [
      'Typ wählbar (Nacht, Sonntag, Feiertag, …)',
      'Faktor oder Pauschal-Aufschlag',
      'Geltungszeitraum und Zielgruppe',
    ],
  },

  // 10. Workflows & Tickets
  {
    id: 'workflows',
    title: 'Workflows',
    category: 'Workflows & Tickets',
    description:
      'Vordefinierte Abläufe für wiederkehrende Vorgänge: Onboarding-Checkliste, Offboarding, Projekt-Abschluss, Urlaubsfreigabe. Jeder Workflow besteht aus mehreren Schritten mit Zuständigen und Fristen.',
    highlights: [
      'Vorlagen als Blaupausen',
      'Instanz mit Zuweisung pro Schritt starten',
      'Schritte abhaken mit Notiz und Zeitstempel',
      'Vollständiger Verlauf einsehbar',
    ],
  },
  {
    id: 'tickets',
    title: 'Tickets',
    category: 'Workflows & Tickets',
    description:
      'Leichtgewichtiges Ticket-System für interne Aufgaben, Bugs und Verbesserungsvorschläge. Mit Typ, Priorität, Status, Zuweisung, Kommentaren und Mentions. Anhänge und Verknüpfungen möglich.',
    highlights: [
      'Typen: Bug, Feature, Aufgabe, Frage',
      'Kommentare mit Mentions',
      'Anhänge und Verknüpfungen',
      'Status-Wechsel über Buttons oder Drag-Drop',
    ],
  },
  {
    id: 'kanban',
    title: 'Kanban-Board',
    category: 'Workflows & Tickets',
    description:
      'Ticket-Übersicht als klassisches Kanban-Board. Spalten entsprechen Status (Backlog, In Bearbeitung, Review, Erledigt). Drag-and-Drop für Status-Wechsel. Board-Konfiguration nur für Admins.',
    highlights: [
      'Drag-Drop zwischen Spalten',
      'Filter nach Typ, Zuweisung, Tag',
      'Inline-Anlage in der ersten Spalte',
    ],
  },
  {
    id: 'disposition',
    title: 'Disposition',
    category: 'Workflows & Tickets',
    description:
      'Gantt-ähnliche Personalplanung: wer wird wann auf welches Projekt eingeplant? Konflikte mit Urlauben oder Überbuchungen werden hervorgehoben. Admin oder dedizierte Disposition-Rolle erforderlich.',
    highlights: [
      'Drag zum Anlegen einer Zuordnung',
      'Verschieben / Verlängern per Pointer',
      'Konflikt-Warnung bei Doppelbuchung',
      'Wochen- und Monatsansicht',
    ],
  },
  {
    id: 'team-planning',
    title: 'Teamplanung',
    category: 'Workflows & Tickets',
    description:
      'Übersicht über alle Mitarbeitenden der eigenen Teams: heute anwesend, abwesend, geplante Projekte, ausstehende Anträge. Quick-Genehmigung und Drilldown ins Personen-Dossier.',
    highlights: [
      'Heute / Diese Woche Schnellumschalter',
      'Status-Icons mit Tooltip',
      'Klick auf Person öffnet Dossier',
    ],
  },

  // 11. Persönliches
  {
    id: 'profile',
    title: 'Profil',
    category: 'Persönliche Einstellungen',
    description:
      'Eigene Stammdaten, Anzeigename, E-Mail, Passwort, Foto, Zeitzone, Sprache, Push-Benachrichtigungen pro Gerät. 2FA über TOTP einrichten — QR-Code im Authenticator scannen.',
    highlights: [
      'Passwort ändern mit Bestätigung',
      '2FA (TOTP) einrichten',
      'Avatar hochladen oder als Initialen',
      'Browser-Push pro Gerät aktivieren',
    ],
  },
  {
    id: 'appearance',
    title: 'Darstellung',
    category: 'Persönliche Einstellungen',
    description:
      'Theme (hell / dunkel / system), Akzentfarbe, Schriftgröße, Sidebar-Stil, Datums- und Zeitformat. Änderungen werden sofort angewandt.',
    highlights: [
      'Theme: hell / dunkel / system',
      'Akzentfarbe aus Palette',
      'Dichte: kompakt / Standard / luftig',
    ],
  },
  {
    id: 'sidebar',
    title: 'Sidebar',
    category: 'Persönliche Einstellungen',
    description:
      'Pro Modul ein- oder ausblenden, was in der Sidebar erscheint. Überschreibt die Rolle-Defaults für die eigene Person. Reset auf Rollen-Default jederzeit möglich.',
    highlights: ['Toggle pro Modul', 'Reset auf Rollen-Default'],
  },
  {
    id: 'dashboard-templates',
    title: 'Dashboard-Vorlagen',
    category: 'Persönliche Einstellungen',
    description:
      'Vorlagen für initiale Dashboard-Layouts; können Personen oder Rollen zugewiesen werden. Beim ersten Login lädt die Person die Vorlage und kann anschließend frei anpassen. Nur Admin.',
    highlights: ['Vorlage anlegen mit Widget-Konfiguration', 'Zuweisung an Personen oder Gruppen'],
  },
  {
    id: 'ai',
    title: 'KI-Einstellungen',
    category: 'Persönliche Einstellungen',
    description:
      'Anbindung optionaler KI-Assistenten (lokales LLM oder Cloud-Endpoint) für Aufgaben wie Antrags-Begründungen, Zusammenfassungen, Beleg-Klassifikation. Nur Admin.',
    highlights: [
      'Endpoint + API-Key',
      'Modellwahl und Token-Limits',
      'Test-Verbindung für Sanity-Check',
    ],
  },

  // 12. Zugriff & Freigabe
  {
    id: 'entity-access',
    title: 'Stammdaten-Sperren',
    category: 'Zugriff & Freigabe',
    description:
      'Für jedes Stammdaten-Objekt (Kunde, Projekt, Leistung, Produkt) lässt sich definieren, welche Personen oder Rollen darauf zugreifen dürfen. Restriktionen schlagen automatisch auf alle abgeleiteten Listen, Filter, Reports und Aggregationen durch.',
    highlights: [
      'Pro Datensatz Zugriff freigeben / sperren',
      'Bulk-Aktion (gecappte Maximalzahl pro Vorgang)',
      'Audit-Log dokumentiert betroffene IDs',
      'Sperrungen wirken auf Reports und Aggregate',
    ],
  },
  {
    id: 'manual-groups',
    title: 'Manuelle Gruppen',
    category: 'Zugriff & Freigabe',
    description:
      'Frei definierbare Ordner für Kunden, Projekte, Leistungen und Produkte. Jede Gruppe hat einen Namen und eine Farbe; Mitglieder werden manuell zugeordnet. In Tabellen als Sektionen nutzbar („Nach Gruppe gruppieren“).',
    highlights: [
      'Gruppe anlegen mit Name + Farbpalette',
      'Mitglieder per SidePanel oder Bulk-Aktion',
      'Inline-Umbenennen in der Verwalten-Ansicht',
      'Sektion „Nicht gruppiert“ für freie Datensätze',
    ],
  },
]

const CATEGORIES = Array.from(new Set(SECTIONS.map((s) => s.category)))

// Vorhandene Screenshot-PNGs in apps/web/public/guide/screenshots/.
// Wird beim Befuellen von SECTIONS unten als Fallback genutzt: existiert
// eine Datei mit dem Section-Slug, wird sie automatisch angezeigt.
// Neue Screenshots einfach hier eintragen und in den Ordner legen.
const AVAILABLE_SCREENSHOTS = new Set([
  'activity-log',
  'auswertung',
  'calendar',
  'chat',
  'customers',
  'dashboard',
  'documents',
  'entries',
  'invoices',
  'kanban',
  'mail',
  'meetings',
  'personnel',
  'products',
  'projects',
  'requests',
  'services',
  'settings-holidays',
  'settings-profile',
  'settings-roles',
  'settings-users',
  'team-planning',
  'tickets',
  'time-accounts',
  'timer',
  'workflows',
])

// Aliasse: einzelne Section-IDs zeigen auf einen anderen Screenshot
// (z. B. /dashboard.png deckt das Dashboard-Overview-Modul ab).
const SCREENSHOT_ALIAS: Record<string, string> = {
  'dashboard-overview': 'dashboard',
  'widget-registry': 'dashboard',
  teams: 'personnel',
  users: 'settings-users',
  org: 'personnel',
  roles: 'settings-roles',
  holidays: 'settings-holidays',
  blackouts: 'personnel',
  'vacation-rules': 'personnel',
  surcharges: 'personnel',
  profile: 'settings-profile',
  ai: 'settings-profile',
  appearance: 'settings-profile',
  sidebar: 'settings-profile',
  'dashboard-templates': 'dashboard',
  'invoices-detail': 'invoices',
  'invoices-new': 'invoices',
  'invoice-layout': 'invoices',
  'documents-detail': 'documents',
  fetcher: 'documents',
  disposition: 'team-planning',
  absences: 'requests',
  notifications: 'chat',
  reports: 'auswertung',
  stammdaten: 'customers',
  tags: 'customers',
  'entity-access': 'customers',
  'manual-groups': 'customers',
}

function resolveScreenshot(id: string, explicit?: string): string | undefined {
  if (explicit) return explicit
  const alias = SCREENSHOT_ALIAS[id] ?? id
  if (AVAILABLE_SCREENSHOTS.has(alias)) {
    return `/guide/screenshots/${alias}.png`
  }
  return undefined
}

function GuidePage() {
  const [query, setQuery] = useState('')
  const [scrolled, setScrolled] = useState(false)
  // Mobile-Shell (<640): Suche-Sheet + Kapitel-Sprungnav-Sheet hinter den
  // Bottom-Slots (PDF-Download wandert ins Kapitel-Sheet), Titel oben.
  const [searchSheet, setSearchSheet] = useState(false)
  const [chapterSheet, setChapterSheet] = useState(false)
  useMobilePageTitle('Guide')

  // Auth via api.me() — das Session-Cookie ist HttpOnly, deshalb funktioniert
  // ein document.cookie-Check nicht. Während des Ladens nichts rendern, bei
  // ungültiger Session zum Login.
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 320)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const filtered = useMemo(() => {
    if (!query) return SECTIONS
    const q = query.toLowerCase()
    return SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q) ||
        (s.highlights ?? []).some((h) => h.toLowerCase().includes(q)),
    )
  }, [query])

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, GuideSection[]>>((acc, s) => {
      if (!acc[s.category]) acc[s.category] = []
      acc[s.category].push(s)
      return acc
    }, {})
  }, [filtered])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const visibleCategories = CATEGORIES.filter((c) => grouped[c]?.length)

  const scrollToCategory = (cat: string) => {
    const slug = `cat-${cat.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`
    const el = document.getElementById(slug)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + Kapitel → Sprungnav-
          Sheet — kein ＋ (read-only, Mapping §3). */}
      <MobileBottomSlots>
        <BarSlot
          label="Suche"
          icon={<Search className="h-5 w-5" />}
          active={query !== ''}
          ariaLabel="Im Guide suchen"
          ariaHasPopup
          onClick={() => setSearchSheet(true)}
        />
        <BarSlot
          label="Kapitel"
          icon={<BookOpen className="h-5 w-5" />}
          ariaLabel="Zu einem Kapitel springen"
          ariaHasPopup
          onClick={() => setChapterSheet(true)}
        />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe query wie die Kopf-Suche —
          Treffer filtern live hinter dem Sheet. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Im Guide suchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* Kapitel-Sheet (<640): Sprungnav zu den bestehenden Kapitel-Ankern
          (scrollToCategory) + Handbuch-PDF als letzte Zeile. */}
      <SidePanel open={chapterSheet} onClose={() => setChapterSheet(false)} title="Kapitel">
        <div className="flex flex-col gap-1">
          {visibleCategories.map((cat) => (
            <SheetRow
              key={cat}
              active={false}
              label={`${cat} (${grouped[cat].length})`}
              onClick={() => {
                setChapterSheet(false)
                scrollToCategory(cat)
              }}
            />
          ))}
          <div className="mt-3 border-t border-border-subtle pt-3">
            <SheetRow
              active={false}
              label="Handbuch (PDF) öffnen"
              onClick={() => window.open('/guide/embertime-handbuch.pdf', '_blank', 'noreferrer')}
            />
          </div>
        </div>
      </SidePanel>

      {/* Header + Toolbar — <640 übernimmt die Shell (Titel oben, Suche/
          Kapitel/PDF unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <div className="flex phone:hidden flex-wrap lg:flex-nowrap items-center gap-2 pb-3 border-b border-border-subtle">
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <BookOpen className="h-5 w-5 text-accent" />
          <h1 className="text-lg font-medium tracking-tight text-text-primary">Guide</h1>
          <span className="text-xs text-text-tertiary ml-1 hidden sm:inline">
            Anwendungs-Handbuch
          </span>
        </div>
        <div className="hidden lg:block flex-1" />
        <div className="flex items-center gap-2 shrink-0 flex-1 lg:flex-none">
          <div className="relative flex-1 lg:flex-none lg:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Im Guide suchen …"
              className="w-full text-sm rounded-md border border-border-default bg-bg-elevated pl-8 pr-3 py-1.5 placeholder:text-text-tertiary/60 focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <a
            href={`${import.meta.env.BASE_URL}guide/embertime-handbuch.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 text-accent hover:bg-accent/15 px-3 py-1.5 text-xs font-medium transition-colors"
            title="Handbuch als PDF öffnen"
          >
            <FileText className="h-3.5 w-3.5" /> Handbuch (PDF)
          </a>
        </div>
      </div>

      {/* Kapitel-Quick-Nav (Chips) — <640 übernimmt das Kapitel-Sheet. */}
      {visibleCategories.length > 1 && (
        <div className="flex phone:hidden flex-wrap gap-1.5 pb-1">
          {visibleCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => scrollToCategory(cat)}
              className="text-xs rounded-full border border-border-subtle bg-bg-elevated/40 px-3 py-1 text-text-secondary hover:border-accent/40 hover:text-accent transition-colors"
            >
              {cat}
              <span className="ml-1.5 text-text-tertiary">{grouped[cat].length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Intro / Empty State */}
      {filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-12 text-center">
          <BookOpen className="h-10 w-10 mx-auto text-text-tertiary/60 mb-3" strokeWidth={1.5} />
          <p className="text-text-secondary">Keine Treffer für „{query}“.</p>
          <p className="text-xs text-text-tertiary mt-1">
            Andere Begriffe ausprobieren oder den Filter leeren.
          </p>
        </div>
      )}

      {/* Grouped sections */}
      {visibleCategories.map((cat) => (
        <section
          key={cat}
          id={`cat-${cat.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
          className="space-y-3 scroll-mt-20"
        >
          <h2 className="text-xs uppercase tracking-wider text-text-tertiary font-medium px-1">
            {cat}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped[cat].map((s) => {
              const shot = resolveScreenshot(s.id, s.screenshot)
              return (
                <article
                  key={s.id}
                  id={s.id}
                  className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden hover:border-accent/40 transition-colors scroll-mt-20"
                >
                  {shot && (
                    <div className="aspect-video bg-bg-overlay/40 overflow-hidden">
                      <img
                        src={shot}
                        alt={s.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Wenn die Datei doch fehlt, blendet sich das Bild aus
                          ;(e.currentTarget.parentElement as HTMLElement | null)?.remove()
                        }}
                      />
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <h3 className="text-sm font-medium text-text-primary">{s.title}</h3>
                    <p className="text-xs text-text-secondary leading-relaxed whitespace-pre-line">
                      {s.description}
                    </p>
                    {s.highlights && s.highlights.length > 0 && (
                      <ul className="text-[11px] text-text-tertiary space-y-0.5 pt-1">
                        {s.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-1.5">
                            <span className="text-accent mt-0.5">›</span>
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      ))}

      {/* Floating "Zum Anfang" button */}
      {scrolled && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-bg-elevated/95 backdrop-blur shadow-lg text-accent hover:bg-accent/10 px-3.5 py-2 text-xs font-medium transition-colors"
          title="Zum Anfang scrollen"
        >
          <ArrowUp className="h-3.5 w-3.5" /> Zum Anfang
        </button>
      )}
    </div>
  )
}
